import { useCallback, useEffect, useMemo, useState } from 'react';
import { PersonsV3 } from '@frontend/api-person';
import { PaymentsFeatureFlags, useLocationSurchargeState, useMerchants } from '@frontend/payments-hooks';
import { useLoadJustifi } from '@frontend/payments-justifi-wrapper';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { useModalControl, useMultiStepControl } from '@frontend/design-system';
import { isPaidInvoice } from '../utils/invoice';
import { makeLifecycleHooks } from '../utils/lifecycle';
import {
  CollectPaymentMultiActiveGateway,
  UseCollectPaymentMultiStepProps,
  UseCollectPaymentMultiProps,
  CollectPaymentMultiFlow,
  CollectPaymentMultiState,
  CollectPaymentMultiSteps,
} from './collect-payment-multi.types';
import { useInvoiceQuery } from './hooks/use-invoice-query';
import { useStepTrackingStoreShallow, useStepTrackingStoreUuid } from './utils/step-tracking-store';
import { getStepDataByFlow, isPaymentMethodStep, PAYMENT_MULTI_STEPS } from './utils/steps';

export const DEFAULT_MIN_WIDTH = 654;

export const useCollectPaymentMulti = <F extends CollectPaymentMultiFlow = 'create'>(
  collectProps: UseCollectPaymentMultiProps<F>,
  multiStepProps: UseCollectPaymentMultiStepProps = {}
): CollectPaymentMultiState<F> => {
  const {
    personId,
    invoiceId: initialInvoiceId,
    locationId: overrideLocationId,
    onPaymentSuccess: onPaymentSuccessCallback,
    lifecycle,
    onInvoiceCreated,
    onInvoiceSelected,
    onPaymentCompletedAsync,
    onLocationHasNoPayments,
    trackingId = 'payments:nwx:collect-payment-multi',
    origin,
    isResponsiveMode: isResponsiveModeProp,
    topOffset: topOffsetProp,
    ...rest
  } = collectProps || {};

  const [minWidth, setMinWidth] = useState(DEFAULT_MIN_WIDTH);
  const [isFocused, setIsFocused] = useState(false);
  const [isResponsiveMode, setIsResponsiveMode] = useState(isResponsiveModeProp || false);
  const [topOffset, setTopOffset] = useState(topOffsetProp || 0);
  const hasResponsiveModeProp = typeof isResponsiveModeProp === 'boolean';

  const { getFeatureFlagValue } = useAppFlagStore();

  const { uuid: modalInstanceUuid } = useStepTrackingStoreUuid();

  const { initiateModalState, finalizeModalState } = useStepTrackingStoreShallow(
    'initiateModalState',
    'finalizeModalState'
  );

  const resetMinWidth = useCallback(() => {
    setMinWidth(DEFAULT_MIN_WIDTH);
  }, []);

  const initialFlow = rest.flow || 'create';

  const beforeUnmountCallback = useCallback(() => {
    finalizeModalState({
      status: 'cancel',
    });
  }, [finalizeModalState]);

  const multiStep = useMultiStepControl({
    ...multiStepProps,
    isResponsiveMode,
    stepper: {
      show: false,
      ...multiStepProps?.stepper,
    },
    onChangeStep: (step, move) => {
      if (typeof multiStepProps.onChangeStep === 'function') {
        const proceed = multiStepProps.onChangeStep(step, move);

        if (proceed === false) {
          return;
        }
      }

      if (step === PAYMENT_MULTI_STEPS.selectTerminal && move.movement === -1) {
        // allow redirect for register terminal > select terminal.
        // Do nothing
      } else if (isPaymentMethodStep(step) && move.movement < 0) {
        // going back from payment step, go to payment methods
        multiStep.goPrevStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
        return false;
      }

      return;
    },
    onCancel: () => {
      if (typeof multiStepProps.onCancel === 'function') {
        multiStepProps.onCancel();
      }

      /**
       * custom logic here
       */
      beforeUnmountCallback();
    },

    onComplete: () => {
      if (typeof multiStepProps.onComplete === 'function') {
        multiStepProps.onComplete();
      }

      /**
       * custom logic here
       */
      finalizeModalState({
        status: 'complete',
      });
    },
  });

  const [activeInvoiceId, setActiveInvoiceId] = useState<string | null>(initialInvoiceId ?? null);
  const [flow, setFlow] = useState<CollectPaymentMultiFlow>(initialFlow);
  const [hasCompletedPayment, setHasCompletedPayment] = useState(false);

  const { accessibleLocationIds: allLocations, selectedLocationIds } = useAppScopeStore();
  const selectedLocationId = selectedLocationIds[0];

  /**
   * If we have override we don't want to query an invoice from other locations.
   *
   * Otherwise, prioritize selected location, but use all locations. Then if given an invoice ID
   * from another location, we can query it. The invoice's location will then become the highest priority.
   */
  const activeLocationIds = useMemo(() => {
    if (overrideLocationId) {
      return [overrideLocationId];
    }
    /**
     * MultiQueryUtils > LocationID has better context than allLocations.
     * allLocations should always be consulted last.
     */
    return [selectedLocationId, ...(allLocations || [])].filter(Boolean) as string[];
  }, [allLocations, selectedLocationId, overrideLocationId]);

  const { invoice, ...invoiceQuery } = useInvoiceQuery(activeInvoiceId, activeLocationIds);

  const activeLocationId = useMemo(() => {
    return invoice?.locationId || overrideLocationId || selectedLocationId;
  }, [overrideLocationId, selectedLocationId, invoice?.locationId]);

  /* TODO: we want to use only one useMerchants call below, but we need to refactor the logic to check 
  if the activeLocationId is in the selectedLocationIds and prepend it to the list if it is not.
  The above has to be done as the activeLocationId from invoice.locationId might not be 
  in the selectedLocationIds if we land on the invoice details page directly using a link or 
  if we get the invoice from a list based on a personId. */
  const { allMerchantsLoaded, anyLocationHasPayments } = useMerchants('selectedLocationIds');
  const { stripeLocationId, merchant, stripeCreatedNotOnboarded, isDemoLocation } =
    useMerchants(activeLocationId)['firstLocation'];

  const activeProcessor = useMemo(() => {
    return merchant?.processors?.find((p) => p.isActive);
  }, [merchant]);

  const paymentSetupModalControl = useModalControl();

  const activePersonId = invoice?.person?.id || personId;

  const activeLocationIdProp = useMemo(() => {
    return activeLocationId ? [activeLocationId] : [];
  }, [activeLocationId]);

  const hasJustifyOnboardingEnabled = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.EnableJustifyOnboarding,
    locationIds: activeLocationIdProp,
  });

  const { data: person, ...personQuery } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    { locationIds: activeLocationIdProp, personId: activePersonId! },
    { enabled: !!activePersonId && !!activeLocationId }
  );

  const enableACHOnFileStripe = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ACHOnFile,
    locationIds: activeLocationIdProp,
  });

  const enableShareInEmail = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.ShareInEmail,
    locationIds: [invoice?.locationId ?? ''],
  });

  const { isAllEnabled: surchargeEnabled, firstPercentValue: surchargePercentValue } =
    useLocationSurchargeState(activeLocationIdProp);

  const activeGateway: CollectPaymentMultiActiveGateway = useMemo(() => {
    if (hasJustifyOnboardingEnabled && activeProcessor?.processorType?.toLowerCase() === 'justifi') {
      return 'justifi';
    }
    if (stripeLocationId) {
      return 'stripe';
    }

    // show "Stripe account not setup" errors.
    return 'stripe';
  }, [stripeLocationId, hasJustifyOnboardingEnabled, activeProcessor]);

  const changeFlow = useCallback(
    (newFlow: CollectPaymentMultiFlow, goToStepName?: string) => {
      setFlow(newFlow);
      const steps = getStepDataByFlow(newFlow, multiStep.steps);

      multiStep.setSteps(steps);

      if (goToStepName) {
        multiStep.setActiveStep(goToStepName);
      } else if (steps[0]) {
        multiStep.setActiveStep(steps[0].id);
      }
    },
    [initialInvoiceId, multiStep.steps]
  );

  const resetState = useCallback(() => {
    setActiveInvoiceId(initialInvoiceId ?? null);
    changeFlow(initialFlow);
  }, [initialInvoiceId, multiStep.steps]);

  useEffect(() => {
    changeFlow(initialFlow);
  }, [initialFlow]);

  useEffect(() => {
    if (!!initialInvoiceId && flow === 'create') {
      console.error(
        'useCollectPaymentMulti: props.invoiceId should not be used in "create" flow. If you are managing your own selectedInvoiceId state, please reset it inside your "onClickCreateInvoice" handler.'
      );
    }
    setActiveInvoiceId(initialInvoiceId ?? null);
  }, [initialInvoiceId]);

  const renderSelectPaymentsTemplate =
    collectProps.flow === 'insert' ? collectProps.renderSelectPaymentsTemplate : undefined;

  const onPaymentSuccess = useCallback(() => {
    setHasCompletedPayment(true);

    if (typeof onPaymentSuccessCallback === 'function') {
      onPaymentSuccessCallback();
    }
  }, [onPaymentSuccessCallback]);

  const invoiceIsPaid = isPaidInvoice(invoice);

  const lifecycleHooks = useMemo(() => {
    return makeLifecycleHooks(lifecycle);
  }, [lifecycle]);

  useEffect(() => {
    if (!isFocused) {
      /**
       * Prevent errors if hook is being invoked without being active
       */
      return;
    }
    if (invoiceIsPaid && !hasCompletedPayment) {
      if (typeof onPaymentCompletedAsync == 'function') {
        setHasCompletedPayment(true);
        onPaymentCompletedAsync(invoice);
      } else {
        if (multiStep.steps.length > 0) {
          if (flow === 'insert') {
            multiStep.goToStep({ orIndex: 0 });
          } else if (multiStep.activeStep !== PAYMENT_MULTI_STEPS.paymentMethods) {
            multiStep.goToStep({ orStep: PAYMENT_MULTI_STEPS.paymentMethods });
          }
        }
        onPaymentSuccess();
      }
    }
  }, [invoiceIsPaid, isFocused]);

  useLoadJustifi(['paymentMethod'], activeGateway === 'justifi' && isFocused);

  useEffect(() => {
    /**
     * When this modal instance is focused (rendered) we want to initiate the modal state.
     * This is because multiple instances of useCollectPaymentMulti() run at once
     * or not.
     */
    if (isFocused) {
      initiateModalState({
        uuid: modalInstanceUuid,
        flow,
        firstStep: multiStep.activeStep as CollectPaymentMultiSteps,
        activeGateway,
      });
    }
  }, [isFocused]);

  return {
    // direct callback passthrough
    ...rest,
    onInvoiceCreated,
    onInvoiceSelected,
    renderSelectPaymentsTemplate,
    onLocationHasNoPayments,

    // wrapped callbacks
    onPaymentSuccess,
    beforeUnmountCallback,

    // // behind the scenes methods,
    resetState,

    // // direct prop passthrough
    trackingId,
    multiStep,
    origin,

    // // controlled state
    activeInvoiceId,
    setActiveInvoiceId,
    modalInstanceUuid,
    flow,
    setFlowAndStep: changeFlow,
    minWidth,
    setMinWidth,
    resetMinWidth,
    isFocused,
    setIsFocused,

    // derived state
    invoice,
    invoiceQuery,
    invoiceIsPaid,
    hasCompletedPayment,

    activeGateway,
    activePersonId,
    person,
    personQuery,
    activeLocationIds,
    activeLocationId,
    merchant,
    activeProcessor,
    isDemoLocation,
    allMerchantsLoaded,
    anyLocationHasPayments,
    paymentSetupModalControl,
    surchargeEnabled,
    surchargePercentValue,
    stripeData: {
      stripeLocationId,
      enableACHOnFile: enableACHOnFileStripe,
      enableShareInEmail,
      stripeCreatedNotOnboarded,
      canRegisterTerminals: true,
    },
    justifiData: {
      hasOnboardingEnabled: hasJustifyOnboardingEnabled,
      enableShareInEmail,
      canRegisterTerminals: true,
    },
    lifecycleHooks,
    isResponsiveMode,
    setIsResponsiveMode,
    topOffset,
    setTopOffset,
    hasResponsiveModeProp,
  };
};
